// @ts-nocheck

import classNames from 'classnames';
import deepmerge from 'deepmerge';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import * as React from 'react';
import { ContextsService } from '../../../../../../shared/Contexts.service';
import { DEFAULT_DRUID_FILTERS_LIST } from '../../../../../../shared/constants/defaultDruidDimensions';
import { OR_TYPE } from '../../../../common/support/utils/druid/filters/OrFilter';
import { SUMMARY_PAGE_STYLE_VARIANT } from '../../../../main/screens/ScreenNewSummary/newSummaryConstants';
import { generateCsvExportModalOpenPathName } from '../../../../main/services/CsvExportQueryService';
import { ImageService } from '../../../../main/services/ImageService/ImageService';
import type { Filter, Filters } from '../../../../main/types/druidTypes';
import { FILTER_DIMENSIONS } from '../../../support/eo_mapping/dimensions';
import {
  convertNativeFiltersToDisplayFormat,
  getListOfDimensionsAndValues,
} from '../../../support/utils/druid/nativeQueries';
import { deleteFilter } from '../../../support/utils/druid/updateQuery';
import { AddFieldButton, Checkbox, GranularitySelector, HoverButton } from '../../core';
import FilterRow from './FilterRow';
import { formatFilterOptions, objectKeysToSelectFormat } from './utils';

type Props = {
  allFilters: Filters;
  filter?: Filter;
  requiredFilter?: Filter;
  unique?: boolean;
  updateUnique?: () => void;
  disableUniqueToggle?: boolean;
  organic?: boolean;
  updateOrganic?: (arg0: Record<string, any>, arg1: string | null | undefined) => void;
  disableOrganicToggle?: boolean;
  webOnly?: boolean;
  updateWebOnly?: () => void;
  disableWebOnlyToggle?: boolean;
  hasExportButton?: boolean;
  hasWebOnlyToggle?: boolean;
  hasUniqueToggle?: boolean;
  hasOrganicToggle?: boolean;
  dimensionsToDisplay: string[];
  customLabelMap?: Record<string, any>;
  fetchFilters: (arg0: string) => void;
  updateQuery: (arg0: Record<string, any>) => void;
  testid: string;
  updateGranularity?: (arg0: Record<string, any>) => void;
  granularity?: string;
  updateBlockedEventIndicator?: (arg0: string) => void;
  context?: string;
  dimensions?: Array<string>;
  hasFraudDescriptionToggle?: boolean;
  showFraudDescription?: boolean;
  toggleFraudDescription?: (arg0: Record<string, any>) => void;
  styleVariant?: string;
  namespace?: string;
};
type State = {
  emptyField: boolean;
};

class FilterRows extends React.Component<Props, State> {
  static propTypes = {
    /*
     * Filters used in query sent to druid
     */
    filter: PropTypes.object,

    /*
     * Filter dimension/value options fetched from druid
     */
    allFilters: PropTypes.object,

    /*
     * Filters that are uneditable
     */
    requiredFilter: PropTypes.object,

    /*
     * Whether 'unique' box is checked or not (state maintained on DruidQueryBuilder parent)
     */
    unique: PropTypes.bool,

    /*
     * Callback to update 'unique' state (and therefore query) on DruidQueryBuilder parent
     */
    updateUnique: PropTypes.func,

    /**
     * Whether the 'unique' box should be disabled or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    disableUniqueToggle: PropTypes.bool,

    /**
     * Whether the 'unique' box should be displayed or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    hasUniqueToggle: PropTypes.bool,

    /**
     * Whether the 'web only' box should be displayed or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    hasWebOnlyToggle: PropTypes.bool,

    /*
     * Whether 'organic' box is checked or not (state maintained on DruidQueryBuilder parent)
     */
    organic: PropTypes.bool,

    /*
     * Callback to update 'organic' state (and therefore query) on DruidQueryBuilder parent
     */
    updateOrganic: PropTypes.func,

    /**
     * Whether the 'organic' box should be disabled or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    disableOrganicToggle: PropTypes.bool,

    /**
     * Whether the 'organic' box should be displayed or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    hasOrganicToggle: PropTypes.bool,

    /**
     * Whether the 'fraud description' box should be displayed or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    hasFraudDescriptionToggle: PropTypes.bool,

    /**
     * Whether the 'fraud description' box should be checked or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    showFraudDescription: PropTypes.bool,

    /**
     * Whether the 'Split SKAN Data' box should be displayed or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    hasSplitSkanDataToggle: PropTypes.bool,

    /**
     * Whether the 'Split SKAN Data' box should be checked or not (is a prop passed to
     * DruidQueryBuilder parent)
     */
    splitSkanData: PropTypes.bool,

    /**
     * Callback to update 'Split SKAN Data' state on DruidQueryBuilder parent
     */
    updateSkanSplit: PropTypes.func,

    /**
     * Callback to update 'fraud description' state on DruidQueryBuilder parent
     */
    toggleFraudDescription: PropTypes.func,

    /**
     * If the DruidQueryBuilder parent has an export button (which is passed in as a child
     * to it), the 'Unique' checkbox will need to be moved over to make room
     */
    hasExportButton: PropTypes.bool,

    /**
     * (optional) Overrides the default druid filters list
     */
    dimensionsToDisplay: PropTypes.array,

    /**
     * (optional) Overrides the default label mapping for dimensions and filters
     */
    customLabelMap: PropTypes.object,

    /**
     * method that fetches filter values for a given dimension
     */
    fetchFilters: PropTypes.func,

    /**
     * method that updates blockedEventIndicator value
     */
    updateBlockedEventIndicator: PropTypes.func,

    /**
     * dimensions selected in the query - used to add fraud_names / fraud_codes when a filter is selected on one of these dimensions
     */
    dimensions: PropTypes.array,

    /**
     * (optional) Shareable Namespace name
     */
    namespace: PropTypes.string,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      emptyField: false,
    };
  }

  /**
   * Deletes a filter row, or removes empty field
   * Sends updateFilters only when deleting a non-empty row
   * @param {Object} row - object representing the filter row
   */
  deleteFilterRow = (row: Record<string, any>) => {
    if (!row.dimension) {
      return this.setState({
        emptyField: false,
      });
    }

    this.props.updateQuery({
      filter: deleteFilter(this.props.filter, row),
    });
  };

  setEmptyField = (emptyField: boolean) => {
    this.setState({
      emptyField,
    });
  };

  render() {
    const {
      allFilters,
      filter,
      requiredFilter,
      testid,
      dimensionsToDisplay,
      customLabelMap,
      organic,
      updateBlockedEventIndicator,
      context,
      updateOrganic,
      updateWebOnly,
      dimensions,
      hasFraudDescriptionToggle,
      showFraudDescription,
      toggleFraudDescription,
      hasSplitSkanDataToggle,
      splitSkanData,
      updateSkanSplit,
      styleVariant,
      hasOrganicToggle,
      hasUniqueToggle,
      hasWebOnlyToggle,
    } = this.props;
    const copiedFilters = Object.assign({}, allFilters);

    let combinedFilters = copiedFilters;

    if (copiedFilters && requiredFilter) {
      combinedFilters = deepmerge(copiedFilters, getListOfDimensionsAndValues(requiredFilter));
    }

    /** filter object: an object with 'label' and 'value' properties
     * filter_types:
     * Array of filter objects, represents what filters should be in the
     * filters drop-down for each row
     */
    const filterTypes = R.pick(dimensionsToDisplay || DEFAULT_DRUID_FILTERS_LIST, combinedFilters);
    const filter_types = objectKeysToSelectFormat(filterTypes, customLabelMap) || [];

    /** filter_options:
     * Object where each property represents a possible filter 'value'
     * and the property values are each arrays of filter objects.
     * These objects represent the values for that given filter that should
     * be displayed in the options drop-down
     */
    const filter_options = formatFilterOptions(combinedFilters, customLabelMap) || {};
    // Array of objects, each object representing that specific FilterRow
    let displayFilters = [];

    if (
      organic &&
      filter &&
      filter.type === OR_TYPE &&
      filter.fields[0] &&
      filter.fields[0].fields &&
      filter.fields[0].fields[0].dimension === FILTER_DIMENSIONS.attributed
    ) {
      displayFilters = convertNativeFiltersToDisplayFormat(filter.fields[1], requiredFilter) || [];
    } else {
      displayFilters = convertNativeFiltersToDisplayFormat(filter, requiredFilter) || [];
    }

    if (this.state.emptyField) {
      displayFilters.push({});
    }

    // show GranularitySelector for tables and timeseries (but not cohort modal)
    const isTableContext = ContextsService.isTableContext(context);
    const isTimeSeriesContext = ContextsService.isTimeSeriesContext(context);
    const showGranularitySelector = isTableContext || isTimeSeriesContext;

    return (
      <div>
        {displayFilters &&
          displayFilters
            .filter((row) => row)
            .map((row, i) => {
              const starter = i === 0 ? 'where' : 'and';
              return (
                <FilterRow
                  key={i}
                  i={i}
                  row={row}
                  starter={starter}
                  requiredFilter={requiredFilter}
                  filter_types={filter_types}
                  filter_options={filter_options}
                  customLabelMap={customLabelMap}
                  displayFilters={displayFilters}
                  deleteFilterRow={this.deleteFilterRow}
                  updateQuery={this.props.updateQuery}
                  fetchFilters={this.props.fetchFilters}
                  filter={this.props.filter}
                  setEmptyField={this.setEmptyField}
                  updateBlockedEventIndicator={updateBlockedEventIndicator}
                  dimensions={dimensions}
                  styleVariant={styleVariant}
                />
              );
            })}
        <div className="analytics-filters__row h_jc-sb h_ai-c">
          <div>
            <span className="analytics-filters__starter" />
            {!this.state.emptyField ? (
              <AddFieldButton
                className={classNames({
                  'analytics-filters__add-filter-margin':
                    displayFilters && displayFilters.length && styleVariant !== SUMMARY_PAGE_STYLE_VARIANT,
                  'analytics-filters__add-filter-margin-modal': styleVariant === SUMMARY_PAGE_STYLE_VARIANT,
                })}
                testid="add-filter"
                onClick={() => this.setEmptyField(true)}
              >
                Add Filter
              </AddFieldButton>
            ) : null}
          </div>
          <div className="h_d-f h_ai-c">
            {hasFraudDescriptionToggle && (
              <Checkbox
                checked={showFraudDescription}
                onChange={(event) => toggleFraudDescription && toggleFraudDescription(event)}
                label="Fraud Description"
                tooltip={`Show additional details on the fraud rule. Only available when comparing by fraud code.`}
                className="analytics-filters__checkbox__fraud-description"
                id={`${testid}__fraud-description`}
              />
            )}
            {hasSplitSkanDataToggle && (
              <Checkbox
                checked={splitSkanData}
                onChange={(event) => updateSkanSplit?.(event)}
                label="Split SKAN Data"
                tooltip={'Toggle to view the split de-duplicated event counts from both SKAN and Non-SKAN sources.'}
                className="analytics-filters__checkbox analytics-filters__checkbox__split-skan-data"
                id={`${testid}__split-skan-data`}
                style={{
                  width: 150,
                }}
              />
            )}
            {hasWebOnlyToggle && (
              <Checkbox
                checked={this.props.webOnly}
                onChange={updateWebOnly}
                disabled={this.props.disableWebOnlyToggle}
                label="Web Only"
                tooltip="Filter data to web links only"
                className="analytics-filters__checkbox"
                id={`${testid}__webOnly`}
                style={{ marginRight: 8 }}
              />
            )}
            {hasOrganicToggle && (
              <Checkbox
                checked={this.props.organic}
                onChange={(event) => updateOrganic && updateOrganic(event)}
                disabled={this.props.disableOrganicToggle}
                label="Organic"
                tooltip={`When \"Organic\" is checked, unattributed data will show (attributed: false). This data is not attributed to any click or impression, and is the same as \"organic\" or \"direct\" data.`}
                className={'analytics-filters__checkbox'}
                id={`${testid}__organic`}
              />
            )}
            {hasUniqueToggle && (
              <Checkbox
                checked={this.props.unique}
                onChange={this.props.updateUnique}
                disabled={this.props.disableUniqueToggle}
                label="Unique"
                tooltip={`When \"Unique\" is checked, these are unique to a user. For example: if 1 user clicks 100 times, it will count as 1. There is no concept of \"unique revenue\"; \"revenue\" is always the sum of all purchase events.`}
                className={'analytics-filters__checkbox'}
                id={`${testid}__unique`}
              />
            )}
            {showGranularitySelector && (
              <GranularitySelector
                updateGranularity={this.props.updateGranularity}
                granularity={this.props.granularity}
                context={this.props.context}
              />
            )}
            {this.props.hasExportButton && (
              <div className="download-csv-button">
                <HoverButton
                  to={generateCsvExportModalOpenPathName(window.location)}
                  textShownOnHover="Download CSV"
                  activeChild={<img src={ImageService.getImageUrl('/static/images/ic_download_active.svg')} />}
                  size="no-miw"
                  type="secondary"
                  className="icon-hover-button"
                  testid={`${testid}__download-csv`}
                >
                  <img src={ImageService.getImageUrl('/static/images/ic_download.svg')} />
                </HoverButton>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default FilterRows;
